/**
 * Is this zone polygon fit to save?
 *
 * Producers draw zones by finger on a phone map or by walking the edge with
 * GPS. Both produce messy input: a shaky finger double-taps the same spot,
 * a GPS walk loops back over itself, a slip of the thumb draws a 2 m "zone"
 * or one that covers half the province. Such a shape breaks things far from
 * where it was drawn — the tracking geofence is derived from the boundary
 * polygon, presence checks run point-in-polygon against stage zones, and a
 * bow-tie has no meaningful inside at all.
 *
 * This validator runs client-side before saving (web, crew app, producer
 * app) and is mirrored by a server trigger, which is why every limit is a
 * named, exported constant the SQL can quote verbatim. Pure functions only:
 * no map library, no I/O.
 */

import type { ZoneAreaType } from "../zonePalette";

/** A GeoJSON position: [lng, lat] (an optional altitude is ignored). */
export type LngLatPosition = ReadonlyArray<number>;

/** A linear ring: closed (first === last) when it comes from GeoJSON. */
export type LngLatRing = ReadonlyArray<LngLatPosition>;

/**
 * Fewest positions a closed ring may have: three corners plus the repeated
 * closing position — a triangle. GeoJSON (RFC 7946 §3.1.6) sets the same floor.
 */
export const ZONE_POLYGON_MIN_POSITIONS = 4;

/**
 * Most positions a closed ring may have. A GPS walk around a large site at
 * one fix per few metres stays well under this; anything above it is a
 * recording left running, and the self-intersection check is O(n²).
 */
export const ZONE_POLYGON_MAX_POSITIONS = 500;

/** Smallest zone, in m². Below this it is a mis-tap, not a place crew can stand. */
export const ZONE_MIN_AREA_SQUARE_METERS = 20;

/** Largest stage zone / media office / crew catering area, in m² (2 km²). */
export const ZONE_MAX_AREA_SQUARE_METERS = 2_000_000;

/**
 * Largest festival boundary, in m² (50 km²). The boundary frames the whole
 * site — campings and parking included — so it gets far more room than the
 * zones inside it.
 */
export const FESTIVAL_BOUNDARY_MAX_AREA_SQUARE_METERS = 50_000_000;

/**
 * Sphere radius for the area maths: the WGS84 equatorial radius, as used by
 * the spherical-excess method below (classic @turf/area). Exported so the
 * server mirror computes the exact same number.
 */
export const ZONE_AREA_EARTH_RADIUS_METERS = 6378137;

/** Every limit in one place, keyed the way the validator reads them. */
export const ZONE_POLYGON_LIMITS = {
  minPositions: ZONE_POLYGON_MIN_POSITIONS,
  maxPositions: ZONE_POLYGON_MAX_POSITIONS,
  minAreaSquareMeters: ZONE_MIN_AREA_SQUARE_METERS,
  maxAreaSquareMeters: {
    stage_zone: ZONE_MAX_AREA_SQUARE_METERS,
    media_office: ZONE_MAX_AREA_SQUARE_METERS,
    crew_catering: ZONE_MAX_AREA_SQUARE_METERS,
    festival_boundary: FESTIVAL_BOUNDARY_MAX_AREA_SQUARE_METERS,
  },
} as const satisfies {
  minPositions: number;
  maxPositions: number;
  minAreaSquareMeters: number;
  maxAreaSquareMeters: Record<ZoneAreaType, number>;
};

/**
 * Why a polygon was refused — one stable code per failure, for UI copy and
 * SQL parity. The list is the single source: the type, the reader of the
 * server's refusal (serverZonePolygonRejection) and the UI copy tests all
 * come from it, so a new code cannot be missed by one of them.
 */
export const ZONE_POLYGON_REJECTIONS = [
  "not-a-polygon",
  "not-closed",
  "too-few-corners",
  "too-many-corners",
  "self-intersecting",
  "too-small",
  "too-large",
] as const;

export type ZonePolygonRejection = (typeof ZONE_POLYGON_REJECTIONS)[number];

/**
 * Outcome of validateZonePolygon. Narrow with `result.ok === false` (or
 * `=== true`) rather than `!result.ok`: web and mobile compile without
 * strictNullChecks, where truthiness does not narrow this union.
 */
export type ZonePolygonValidation =
  | { ok: true; areaSquareMeters: number }
  | { ok: false; reason: ZonePolygonRejection };

/**
 * Largest allowed area for an area type. An unknown type (bad data, a type
 * added in SQL before the client knows it) gets the strict zone limit, so a
 * typo can never unlock the much larger boundary allowance.
 *
 * Own keys only: area_type is a plain string in the generated DB types, and a
 * bare lookup of e.g. "toString" or "__proto__" would hit Object.prototype and
 * return a function — against which `area > limit` is always false, silently
 * disabling the max check.
 */
export function maxZoneAreaSquareMeters(areaType: ZoneAreaType): number {
  const limits = ZONE_POLYGON_LIMITS.maxAreaSquareMeters;
  return Object.prototype.hasOwnProperty.call(limits, areaType)
    ? limits[areaType]
    : ZONE_MAX_AREA_SQUARE_METERS;
}

// ─────────────────────────────────────────────────────────────────────────────
// Ring helpers
// ─────────────────────────────────────────────────────────────────────────────

function samePosition(a: LngLatPosition, b: LngLatPosition): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

/**
 * The ring's corners: consecutive duplicates removed (a double tap, a GPS fix
 * taken while standing still) and the closing repeat dropped. Zero-length
 * edges would otherwise count as corners and confuse the intersection test.
 */
function distinctCorners(ring: LngLatRing): LngLatPosition[] {
  const corners: LngLatPosition[] = [];
  for (const position of ring) {
    const last = corners[corners.length - 1];
    if (!last || !samePosition(last, position)) corners.push(position);
  }
  while (corners.length > 1 && samePosition(corners[0], corners[corners.length - 1])) {
    corners.pop();
  }
  return corners;
}

// ─────────────────────────────────────────────────────────────────────────────
// Area
// ─────────────────────────────────────────────────────────────────────────────

const RADIANS_PER_DEGREE = Math.PI / 180;

/**
 * Area enclosed by a ring, in m², always positive (winding order does not
 * matter).
 *
 * Spherical-excess approximation (Chamberlain & Duquette, the method
 * @turf/area implements) on a sphere of ZONE_AREA_EARTH_RADIUS_METERS. Within
 * a fraction of a percent of the ellipsoidal area at festival scale — plenty
 * for limits that are orders of magnitude apart. Accepts a closed or an open
 * ring; fewer than three distinct corners enclose nothing and return 0.
 */
export function ringAreaSquareMeters(ring: LngLatRing): number {
  const corners = distinctCorners(ring);
  const n = corners.length;
  if (n < 3) return 0;

  let total = 0;
  for (let i = 0; i < n; i++) {
    const previous = corners[(i - 1 + n) % n];
    const current = corners[i];
    const next = corners[(i + 1) % n];
    total +=
      (next[0] - previous[0]) * RADIANS_PER_DEGREE * Math.sin(current[1] * RADIANS_PER_DEGREE);
  }
  return Math.abs((total * ZONE_AREA_EARTH_RADIUS_METERS * ZONE_AREA_EARTH_RADIUS_METERS) / 2);
}

// ─────────────────────────────────────────────────────────────────────────────
// Self-intersection
// ─────────────────────────────────────────────────────────────────────────────

/** Which side of the line a→b point c is on: 1 left, -1 right, 0 on it. */
function orientation(a: LngLatPosition, b: LngLatPosition, c: LngLatPosition): number {
  const cross = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  return Math.sign(cross);
}

/** For p known to be on the line a→b: is it within the segment's extent? */
function withinSegment(a: LngLatPosition, b: LngLatPosition, p: LngLatPosition): boolean {
  return (
    Math.min(a[0], b[0]) <= p[0] &&
    p[0] <= Math.max(a[0], b[0]) &&
    Math.min(a[1], b[1]) <= p[1] &&
    p[1] <= Math.max(a[1], b[1])
  );
}

/**
 * Do segments p1→p2 and q1→q2 share any point at all — a crossing, a touch at
 * an endpoint, or a collinear overlap? Only used for non-adjacent edges, where
 * every one of those makes the ring invalid.
 */
function segmentsIntersect(
  p1: LngLatPosition,
  p2: LngLatPosition,
  q1: LngLatPosition,
  q2: LngLatPosition,
): boolean {
  const o1 = orientation(p1, p2, q1);
  const o2 = orientation(p1, p2, q2);
  const o3 = orientation(q1, q2, p1);
  const o4 = orientation(q1, q2, p2);

  if (o1 !== o2 && o3 !== o4) return true;

  return (
    (o1 === 0 && withinSegment(p1, p2, q1)) ||
    (o2 === 0 && withinSegment(p1, p2, q2)) ||
    (o3 === 0 && withinSegment(q1, q2, p1)) ||
    (o4 === 0 && withinSegment(q1, q2, p2))
  );
}

/**
 * Adjacent edges a→b and b→c always meet at b, and that is fine. They overlap
 * beyond b only when the path turns straight back on itself (a spike): c lies
 * on the line through a and b, on a's side of b.
 */
function doublesBack(a: LngLatPosition, b: LngLatPosition, c: LngLatPosition): boolean {
  if (orientation(a, b, c) !== 0) return false;
  const dot = (b[0] - a[0]) * (c[0] - b[0]) + (b[1] - a[1]) * (c[1] - b[1]);
  return dot < 0;
}

/**
 * Does the ring cross or touch itself anywhere?
 *
 * Every pair of edges is tested, the closing edge (last corner back to the
 * first) included. Adjacent edges may share their common corner and nothing
 * more; any other contact — a crossing, a corner resting on another edge, a
 * collinear overlap — counts. Accepts a closed or an open ring (an open ring
 * is treated as implicitly closed); consecutive duplicate positions are
 * ignored. Coordinates are treated as planar lng/lat, the same plane PostGIS
 * uses for ST_IsValid on a geometry column.
 */
export function hasSelfIntersection(ring: LngLatRing): boolean {
  const corners = distinctCorners(ring);
  const n = corners.length;
  const edgeStart = (i: number) => corners[i];
  const edgeEnd = (i: number) => corners[(i + 1) % n];

  for (let i = 0; i < n; i++) {
    if (doublesBack(corners[(i - 1 + n) % n], corners[i], corners[(i + 1) % n])) return true;
  }

  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      // Edge n-1 closes the ring onto corner 0, so it is adjacent to edge 0.
      if (i === 0 && j === n - 1) continue;
      if (segmentsIntersect(edgeStart(i), edgeEnd(i), edgeStart(j), edgeEnd(j))) return true;
    }
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Validation
// ─────────────────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPosition(value: unknown): value is LngLatPosition {
  if (!Array.isArray(value) || value.length < 2) return false;
  const [lng, lat] = value;
  return (
    typeof lng === "number" &&
    typeof lat === "number" &&
    Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    Math.abs(lng) <= 180 &&
    Math.abs(lat) <= 90
  );
}

/**
 * The geometry of a Feature, or the input itself — the DB holds both a bare
 * Polygon and a Feature wrapping one (see parsePolygon in
 * apps/web/src/types/parsers.ts). Writers store the bare geometry.
 */
export function zoneGeometryOf(input: unknown): unknown {
  return isRecord(input) && input.type === "Feature" ? input.geometry : input;
}

/**
 * The single ring of a Polygon, or of a Feature wrapping one.
 *
 * A zone is exactly one ring; a polygon with holes is refused, not
 * half-checked. The drawing tools never make holes, and accepting them would
 * let this check pass what the database rejects or reads differently:
 * festival_areas.geom runs ST_GeomFromGeoJSON over every ring (a malformed
 * hole fails the save, a misplaced one stores an invalid geometry), and both
 * ST_Area and @turf/area subtract holes from the area reported here.
 */
function zoneRingOf(input: unknown): LngLatPosition[] | null {
  const geometry = zoneGeometryOf(input);
  if (!isRecord(geometry) || geometry.type !== "Polygon") return null;

  const { coordinates } = geometry;
  if (!Array.isArray(coordinates) || coordinates.length !== 1) return null;

  const ring: unknown = coordinates[0];
  if (!Array.isArray(ring) || !ring.every(isPosition)) return null;
  return ring;
}

/**
 * Check a drawn zone before it is saved.
 *
 * Checks run cheapest and most fundamental first, so the reason names the
 * real problem: a malformed shape is reported as such rather than as "too
 * small", and a bow-tie as self-intersecting rather than by its misleading
 * net area. A polygon with holes is "not-a-polygon": a zone is one ring.
 * Corners are counted after dropping consecutive duplicates; the position
 * limits apply to the closed ring (corners + closing position).
 */
export function validateZonePolygon(
  geometry: unknown,
  areaType: ZoneAreaType,
): ZonePolygonValidation {
  const ring = zoneRingOf(geometry);
  if (!ring) return { ok: false, reason: "not-a-polygon" };

  if (ring.length > 0 && !samePosition(ring[0], ring[ring.length - 1])) {
    return { ok: false, reason: "not-closed" };
  }

  const positions = distinctCorners(ring).length + 1;
  if (positions < ZONE_POLYGON_LIMITS.minPositions) return { ok: false, reason: "too-few-corners" };
  if (positions > ZONE_POLYGON_LIMITS.maxPositions)
    return { ok: false, reason: "too-many-corners" };

  if (hasSelfIntersection(ring)) return { ok: false, reason: "self-intersecting" };

  const areaSquareMeters = ringAreaSquareMeters(ring);
  if (areaSquareMeters < ZONE_POLYGON_LIMITS.minAreaSquareMeters)
    return { ok: false, reason: "too-small" };
  if (areaSquareMeters > maxZoneAreaSquareMeters(areaType))
    return { ok: false, reason: "too-large" };

  return { ok: true, areaSquareMeters };
}
